import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Camera, 
  Upload,
  Search,
  Lightbulb,
  AlertTriangle,
  Eye,
  Clock,
  Shield,
  ArrowRight,
  CheckCircle,
  XCircle,
  HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function FeatureDiagnostic() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    navigate(user ? '/diagnostic' : '/auth');
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Diagnostic Photo Travaux | Identifier un Problème | Assistant by Léon</title>
        <meta name="description" content="Envoyez une photo de votre mur, sol ou installation et obtenez un diagnostic instantané. Identification des problèmes et recommandations d'expert par IA." />
        <meta name="keywords" content="diagnostic maison, problème mur, fissure maison, identifier problème travaux, analyse photo maison, expertise bâtiment" />
        <link rel="canonical" href="https://assistantbyleon.fr/fonctionnalite/diagnostic-photo" />
        <meta property="og:title" content="Diagnostic Photo - Identifiez les Problèmes de votre Maison" />
        <meta property="og:description" content="Prenez une photo, Léon identifie le problème et vous dit quoi faire. Diagnostic instantané par intelligence artificielle." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Section 1: Accroche SEO optimisée */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6"
          >
            <Camera className="w-5 h-5 text-purple-400" />
            <span className="text-purple-400 font-medium">Diagnostic photo instantané</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6"
          >
            Identifiez les problèmes{' '}
            <span className="text-gold-500">en une photo</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            Photographiez une <strong className="text-white">fissure</strong>, une <strong className="text-white">tache d'humidité</strong> ou 
            n'importe quel <strong className="text-white">problème dans votre maison</strong>.
            <span className="block mt-2 text-gold-400">Léon analyse et vous dit si c'est grave.</span>
          </motion.p>
        </div>
      </section>

      {/* Section 2: Comment fonctionne le diagnostic photo */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Comment obtenir un diagnostic de votre maison ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto"
          >
            Plus besoin d'attendre un expert pendant des semaines. Léon analyse votre photo en quelques secondes.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                step: '1', 
                title: 'Prenez une photo', 
                desc: 'Photographiez la fissure, la tache ou la zone qui vous inquiète.',
                icon: Camera,
                color: 'purple'
              },
              { 
                step: '2', 
                title: 'Analyse IA', 
                desc: 'Léon examine l\'image et identifie le type de problème.',
                icon: Search,
                color: 'gold'
              },
              { 
                step: '3', 
                title: 'Recommandations', 
                desc: 'Recevez un diagnostic clair avec les actions à envisager.',
                icon: Lightbulb,
                color: 'emerald'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-400`} />
                </div>
                <div className="text-gold-500 font-bold text-sm mb-2">Étape {item.step}</div>
                <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Exemple de diagnostic */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-8"
          >
            Exemple : diagnostic d'une fissure sur un mur intérieur
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-purple-500/20"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Eye className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">Photo soumise : fissure verticale sur mur</h3>
                <p className="text-zinc-500 text-sm">Diagnostic de Léon</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-5 h-5 text-blue-400" />
                  <h4 className="text-white font-semibold">Identification du problème</h4>
                </div>
                <p className="text-zinc-300">Fissure de retrait superficielle, environ 2mm de largeur. Ce type de fissure est généralement causé par le séchage naturel des matériaux.</p>
              </div>
              
              <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  <h4 className="text-amber-400 font-semibold">Point d'attention</h4>
                </div>
                <p className="text-zinc-300">Surveiller l'évolution sur 6 mois. Si la fissure s'élargit ou si de nouvelles fissures apparaissent, consulter un professionnel.</p>
              </div>
              
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <h4 className="text-emerald-400 font-semibold">Recommandation de Léon</h4>
                </div>
                <p className="text-zinc-300">Reboucher avec un enduit de rebouchage souple (type acrylique), puis poncer légèrement avant de repeindre. Coût estimé : 15-30€ de matériel.</p>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-zinc-400 text-sm">Pas de signe de problème structurel</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-zinc-400 text-sm">Réparation simple possible</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Avantages du diagnostic photo */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Pourquoi utiliser le diagnostic photo ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-10 max-w-2xl mx-auto"
          >
            Savoir si un problème est grave avant de paniquer (ou de dépenser inutilement).
          </motion.p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Eye, title: 'Œil d\'expert', desc: 'Analyse professionnelle instantanée', color: 'purple' },
              { icon: AlertTriangle, title: 'Détection précise', desc: 'Identifie le type de problème', color: 'amber' },
              { icon: Clock, title: 'Immédiat', desc: 'Résultat en quelques secondes', color: 'blue' },
              { icon: Shield, title: 'Rassurant', desc: 'Savoir si c\'est grave ou non', color: 'emerald' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card-dark p-5 text-center"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-${item.color}-500/10 flex items-center justify-center`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <h3 className="text-white font-bold mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: CTA Final */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Un problème vous inquiète ?
          </h2>
          <p className="text-zinc-400 mb-8">
            Envoyez une photo et obtenez un diagnostic immédiat.
            <span className="block text-gold-400 mt-2">Gratuit pour votre premier diagnostic.</span>
          </p>
          
          <Button
            onClick={handleCTA}
            className="px-10 py-6 text-lg bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white font-bold"
            data-testid="cta-diagnostic"
          >
            <Camera className="w-5 h-5 mr-2" />
            Envoyer une photo
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-zinc-600 text-xs mt-4">
            Gratuit • Sans engagement • Diagnostic instantané
          </p>
        </motion.div>
      </section>

    </div>
  );
}
